import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { DashShell, Kpi, StatusPill } from "@/components/DashShell";
import { useLang } from "@/i18n";
import { api, fmtErr, inr, API } from "@/api";
import { PLANS_META, QR_PAYMENT } from "@/constants";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const inputCls =
  "w-full border border-[#E5E2D9] bg-white px-4 py-3 text-sm outline-none focus:border-[#D4AF37] transition-colors duration-200";
const labelCls = "block text-xs font-bold uppercase tracking-[0.15em] text-[#5C5C5C] mb-2";

const planName = (id) => PLANS_META.find((p) => p.id === id)?.id.replaceAll("_", " ") || id;

const TABS = ["overview", "users", "investments", "deposits", "withdrawals", "loans", "payouts", "passbook", "offers", "settings"];

const ActionBtns = ({ id, onAction, prefix, approveLabel, rejectLabel }) => (
  <div className="flex gap-2">
    <button
      onClick={() => onAction(id, "approve")}
      className="bg-[#089950] text-white text-xs font-bold px-3 py-1.5 hover:bg-[#067540] transition-colors duration-200"
      data-testid={`${prefix}-approve-${id}`}
    >
      {approveLabel}
    </button>
    <button
      onClick={() => onAction(id, "reject")}
      className="bg-[#E03131] text-white text-xs font-bold px-3 py-1.5 hover:bg-[#b02424] transition-colors duration-200"
      data-testid={`${prefix}-reject-${id}`}
    >
      {rejectLabel}
    </button>
  </div>
);

export default function AdminPanel() {
  const { t } = useLang();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [charts, setCharts] = useState(null);
  const [payment, setPayment] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loans, setLoans] = useState([]);
  const [profile, setProfile] = useState(null);
  const [profileBusy, setProfileBusy] = useState(false);
  const [offerForm, setOfferForm] = useState({ text_en: "", text_gu: "" });
  const [payForm, setPayForm] = useState({ upi_id: "", bank_name: "", account_name: "", account_number: "", ifsc: "", admin_alert_email: "" });
  const [qrBusy, setQrBusy] = useState(false);
  const qrRef = useRef(null);
  const [pbForm, setPbForm] = useState({ user_id: "", amount: "", note: "" });

  const load = async () => {
    try {
      const [ov, us, inv, dep, wd, po, ch, pay, of, ln] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/users"),
        api.get("/admin/investments"),
        api.get("/admin/deposits"),
        api.get("/admin/withdrawals"),
        api.get("/admin/dsa-payouts"),
        api.get("/admin/charts"),
        api.get("/admin/payment-settings"),
        api.get("/admin/offers"),
        api.get("/admin/loans"),
      ]);
      setOverview(ov.data);
      setUsers(us.data);
      setInvestments(inv.data);
      setDeposits(dep.data);
      setWithdrawals(wd.data);
      setPayouts(po.data);
      setCharts(ch.data);
      setPayment(pay.data);
      setOffers(of.data);
      setLoans(ln.data);
      setPayForm({
        upi_id: pay.data.upi_id || "", bank_name: pay.data.bank_name || "",
        account_name: pay.data.account_name || "", account_number: pay.data.account_number || "",
        ifsc: pay.data.ifsc || "", admin_alert_email: pay.data.admin_alert_email || "",
      });
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const act = async (path, id, action, msg) => {
    try {
      await api.post(`/admin/${path}/${id}/status`, { action });
      toast.success(msg || t("msg_success"));
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const actEmi = async (loanId, n, action) => {
    try {
      await api.post(`/admin/loans/${loanId}/emis/${n}/status`, { action });
      toast.success(t("msg_emi_updated"));
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const emiVerifications = loans.flatMap((loan) =>
    (loan.emis || []).filter((e) => e.status === "verification").map((emi) => ({ loan, emi }))
  );

  const openProfile = async (u) => {
    setProfileBusy(true);
    try {
      const r = await api.get(`/admin/users/${u.id}/profile`);
      setProfile(r.data);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setProfileBusy(false);
    }
  };

  const creditReturns = async () => {
    try {
      const { data } = await api.post("/admin/run-nightly");
      toast.success(
        `${t("msg_credited_prefix")} ${data.returns.credited} ${t("msg_credited_suffix")} · Monthly: ${data.monthly.credited} · Maturity: ${data.maturity.closed} · Reminders: ${data.reminders.sent} · EMI: ${data.emi_reminders?.sent ?? 0} · Payouts: ${data.payouts.snapshotted}`
      );
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const addEntry = async () => {
    try {
      await api.post("/admin/passbook-entry", {
        user_id: pbForm.user_id,
        amount: Number(pbForm.amount),
        note: pbForm.note || null,
      });
      toast.success(t("msg_entry_added"));
      setPbForm({ user_id: "", amount: "", note: "" });
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const saveSettings = async () => {
    try {
      await api.post("/admin/payment-settings", payForm);
      toast.success(t("msg_settings_saved"));
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const uploadQr = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setQrBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/admin/payment-qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("msg_qr_updated"));
      await load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setQrBusy(false);
    }
  };

  const addOffer = async () => {
    try {
      await api.post("/admin/offers", { text_en: offerForm.text_en, text_gu: offerForm.text_gu });
      toast.success(t("msg_offer_added"));
      setOfferForm({ text_en: "", text_gu: "" });
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const toggleOffer = async (o) => {
    try {
      await api.put(`/admin/offers/${o.id}`, { text_en: o.text_en, text_gu: o.text_gu || "", active: !o.active });
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const deleteOffer = async (o) => {
    try {
      await api.delete(`/admin/offers/${o.id}`);
      toast.success(t("msg_success"));
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const pendingUsers = users.filter((u) => u.status === "pending");
  const pendingInvs = investments.filter((i) => i.status === "pending");
  const pendingDeps = deposits.filter((d) => d.status === "pending");
  const pendingWds = withdrawals.filter((w) => w.status === "pending");
  const approvedCustomers = users.filter((u) => u.status === "approved" && u.role !== "admin");

  const Table = ({ head, children, testid, empty }) => (
    <div className="bg-white border border-[#E5E2D9] overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#5C5C5C] border-b border-[#E5E2D9]">
            {head.map((h) => (
              <th key={h} className="px-6 py-4 font-bold whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
      {empty && <div className="px-6 py-8 text-center text-[#5C5C5C] text-sm">{empty}</div>}
    </div>
  );

  return (
    <DashShell title={t("adm_title")} subtitle={t("adm_sub")} testid="admin-panel">
      <div className="flex flex-wrap gap-2 mb-10" data-testid="admin-tabs">
        {TABS.map((tb) => (
          <button
            key={tb}
            onClick={() => setTab(tb)}
            data-testid={`admin-tab-${tb}`}
            className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors duration-200 ${
              tab === tb ? "bg-[#1A1A1A] text-[#D4AF37] border-[#1A1A1A]" : "border-[#E5E2D9] text-[#5C5C5C] hover:border-[#D4AF37]"
            }`}
          >
            {t(`tab_${tb}`)}
            {tb === "users" && pendingUsers.length > 0 && <span className="ml-2 bg-[#F59E0B] text-white px-1.5 py-0.5 text-[10px]">{pendingUsers.length}</span>}
            {tb === "investments" && pendingInvs.length > 0 && <span className="ml-2 bg-[#F59E0B] text-white px-1.5 py-0.5 text-[10px]">{pendingInvs.length}</span>}
            {tb === "deposits" && pendingDeps.length > 0 && <span className="ml-2 bg-[#F59E0B] text-white px-1.5 py-0.5 text-[10px]">{pendingDeps.length}</span>}
            {tb === "withdrawals" && pendingWds.length > 0 && <span className="ml-2 bg-[#F59E0B] text-white px-1.5 py-0.5 text-[10px]">{pendingWds.length}</span>}
          </button>
        ))}
      </div>

      {tab === "overview" && overview && (
        <div data-testid="admin-overview">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Kpi label={t("adm_total_business")} value={inr(overview.total_business)} gold testid="kpi-admin-business" />
            <Kpi label={t("adm_customers")} value={overview.customers} testid="kpi-admin-customers" />
            <Kpi label={t("adm_dsa_partners")} value={overview.dsas} testid="kpi-admin-dsas" />
            <Kpi label={t("adm_active_plans")} value={overview.active_investments} testid="kpi-admin-plans" />
            <Kpi label={t("adm_pending_users")} value={overview.pending_users} testid="kpi-admin-pending-users" />
            <Kpi label={t("adm_pending_plans")} value={overview.pending_investments} testid="kpi-admin-pending-plans" />
            <Kpi label={t("adm_pending_deposits")} value={overview.pending_deposits} testid="kpi-admin-pending-deposits" />
            <Kpi label={t("adm_pending_withdrawals")} value={overview.pending_withdrawals} testid="kpi-admin-pending-withdrawals" />
          </div>
          {charts && (
            <div className="grid lg:grid-cols-2 gap-6 mb-8" data-testid="admin-charts">
              <div className="bg-white border border-[#E5E2D9] p-7" data-testid="admin-chart-business">
                <h3 className="font-display text-xl font-bold text-[#1A1A1A] mb-5">{t("adm_charts_business")}</h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={charts.months.map((m, i) => ({ month: m, value: charts.business[i] }))} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="bizFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#D4AF37" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#E5E2D9" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#5C5C5C" }} axisLine={{ stroke: "#E5E2D9" }} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: "#5C5C5C" }} axisLine={false} tickLine={false} width={48}
                        tickFormatter={(v) => `₹${v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                      <Tooltip formatter={(v) => [inr(v), t("adm_charts_business")]} contentStyle={{ border: "1px solid #E5E2D9", borderRadius: 0, fontSize: 13 }} />
                      <Area type="monotone" dataKey="value" stroke="#B5952F" strokeWidth={2.5} fill="url(#bizFill)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="bg-white border border-[#E5E2D9] p-7" data-testid="admin-chart-returns">
                <h3 className="font-display text-xl font-bold text-[#1A1A1A] mb-5">{t("adm_charts_returns")}</h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={charts.months.map((m, i) => ({ month: m, value: charts.returns[i] }))} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke="#E5E2D9" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#5C5C5C" }} axisLine={{ stroke: "#E5E2D9" }} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: "#5C5C5C" }} axisLine={false} tickLine={false} width={48}
                        tickFormatter={(v) => `₹${v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                      <Tooltip formatter={(v) => [inr(v), t("adm_charts_returns")]} contentStyle={{ border: "1px solid #E5E2D9", borderRadius: 0, fontSize: 13 }} />
                      <Bar dataKey="value" fill="#1A1A1A" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
          <button
            onClick={creditReturns}
            className="bg-[#D4AF37] text-[#1A1A1A] px-8 py-4 text-sm font-bold tracking-wide hover:bg-[#1A1A1A] hover:text-[#D4AF37] transition-colors duration-300"
            data-testid="credit-returns-button"
          >
            {t("adm_credit_btn")}
          </button>
        </div>
      )}

      {tab === "users" && (
        <Table testid="admin-users-table" head={[t("d_name"), t("adm_email"), t("d_phone"), t("adm_role"), t("adm_dsa_code"), t("d_status"), t("adm_action")]} empty={users.length === 0 ? t("adm_no_users") : null}>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-[#E5E2D9] last:border-0 hover:bg-[#FDFCF8]" data-testid={`admin-user-row-${u.id}`}>
              <td className="px-6 py-4 font-semibold text-[#1A1A1A]">{u.name}</td>
              <td className="px-6 py-4 text-[#5C5C5C]">{u.email}</td>
              <td className="px-6 py-4 tabular text-[#5C5C5C]">{u.phone || "—"}</td>
              <td className="px-6 py-4 uppercase text-xs font-bold text-[#B5952F]">{u.role}</td>
              <td className="px-6 py-4 tabular font-bold">{u.dsa_code || "—"}</td>
              <td className="px-6 py-4"><StatusPill status={u.status} /></td>
              <td className="px-6 py-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => openProfile(u)}
                    className="border border-[#1A1A1A] text-[#1A1A1A] text-xs font-bold px-3 py-1.5 hover:bg-[#1A1A1A] hover:text-[#D4AF37] transition-colors duration-200"
                    data-testid={`admin-user-view-${u.id}`}
                  >
                    {t("adm_view")}
                  </button>
                  {u.status === "pending" && <ActionBtns id={u.id} prefix="user" approveLabel={t("adm_approve")} rejectLabel={t("adm_reject")} onAction={(id, a) => act("users", id, a)} />}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}

      {tab === "investments" && (
        <Table testid="admin-investments-table" head={[t("adm_customer"), t("d_plan"), t("adm_principal"), t("d_rate"), t("d_expected"), t("d_status"), t("adm_action")]} empty={investments.length === 0 ? t("adm_no_investments") : null}>
          {investments.map((i) => (
            <tr key={i.id} className="border-b border-[#E5E2D9] last:border-0 hover:bg-[#FDFCF8]" data-testid={`admin-investment-row-${i.id}`}>
              <td className="px-6 py-4">
                <div className="font-semibold text-[#1A1A1A]">{i.user_name}</div>
                <div className="text-xs text-[#5C5C5C]">{i.user_email}</div>
              </td>
              <td className="px-6 py-4 capitalize font-semibold">{planName(i.plan_type)}</td>
              <td className="px-6 py-4 tabular">{inr(i.principal)}</td>
              <td className="px-6 py-4 text-[#5C5C5C]">{i.rate_label}</td>
              <td className="px-6 py-4 tabular font-bold text-[#089950]">{inr(i.expected_total)}</td>
              <td className="px-6 py-4"><StatusPill status={i.status} /></td>
              <td className="px-6 py-4">
                {i.status === "pending" ? <ActionBtns id={i.id} prefix="inv" approveLabel={t("adm_approve")} rejectLabel={t("adm_reject")} onAction={(id, a) => act("investments", id, a)} /> : "—"}
              </td>
            </tr>
          ))}
        </Table>
      )}

      {tab === "deposits" && (
        <Table testid="admin-deposits-table" head={[t("adm_customer"), t("d_amount"), t("d_reference_id"), t("adm_note"), t("d_proof"), t("d_status"), t("adm_action")]} empty={deposits.length === 0 ? t("adm_no_deposits") : null}>
          {deposits.map((d) => (
            <tr key={d.id} className="border-b border-[#E5E2D9] last:border-0 hover:bg-[#FDFCF8]" data-testid={`admin-deposit-row-${d.id}`}>
              <td className="px-6 py-4">
                <div className="font-semibold text-[#1A1A1A]">{d.user_name}</div>
                <div className="text-xs text-[#5C5C5C]">{d.user_email}</div>
              </td>
              <td className="px-6 py-4 tabular font-bold">{inr(d.amount)}</td>
              <td className="px-6 py-4 tabular font-semibold text-[#1A1A1A] text-sm" data-testid={`deposit-ref-${d.id}`}>{d.reference_id || "—"}</td>
              <td className="px-6 py-4 text-[#5C5C5C]">{d.note || "—"}</td>
              <td className="px-6 py-4" data-testid={`deposit-proof-${d.id}`}>
                {d.proof_path ? (
                  <a
                    href={`${API}/files/${d.proof_path}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-bold text-[#B5952F] underline hover:text-[#1A1A1A] transition-colors duration-200"
                    data-testid={`deposit-proof-link-${d.id}`}
                  >
                    {t("adm_view_proof")}
                  </a>
                ) : "—"}
              </td>
              <td className="px-6 py-4"><StatusPill status={d.status} /></td>
              <td className="px-6 py-4">
                {d.status === "pending" ? <ActionBtns id={d.id} prefix="dep" approveLabel={t("adm_approve")} rejectLabel={t("adm_reject")} onAction={(id, a) => act("deposits", id, a)} /> : "—"}
              </td>
            </tr>
          ))}
        </Table>
      )}

      {tab === "withdrawals" && (
        <Table testid="admin-withdrawals-table" head={[t("adm_customer"), t("d_amount"), t("adm_bank_details"), t("adm_note"), t("d_status"), t("adm_action")]} empty={withdrawals.length === 0 ? t("adm_no_withdrawals") : null}>
          {withdrawals.map((w) => (
            <tr key={w.id} className="border-b border-[#E5E2D9] last:border-0 hover:bg-[#FDFCF8]" data-testid={`admin-withdrawal-row-${w.id}`}>
              <td className="px-6 py-4">
                <div className="font-semibold text-[#1A1A1A]">{w.user_name}</div>
                <div className="text-xs text-[#5C5C5C]">{w.user_email}</div>
              </td>
              <td className="px-6 py-4 tabular font-bold text-[#E03131]">{inr(w.amount)}</td>
              <td className="px-6 py-4" data-testid={`admin-withdrawal-bank-${w.id}`}>
                {w.bank_details ? (
                  <div className="text-xs leading-5 text-[#5C5C5C]">
                    <div className="font-semibold text-[#1A1A1A]">{w.bank_details.account_holder}</div>
                    <div>{w.bank_details.bank_name}</div>
                    <div className="tabular">A/C {w.bank_details.account_number}</div>
                    <div className="tabular">IFSC {w.bank_details.ifsc}</div>
                  </div>
                ) : "—"}
              </td>
              <td className="px-6 py-4 text-[#5C5C5C]">{w.note || "—"}</td>
              <td className="px-6 py-4"><StatusPill status={w.status} /></td>
              <td className="px-6 py-4">
                {w.status === "pending" ? <ActionBtns id={w.id} prefix="wd" approveLabel={t("adm_approve")} rejectLabel={t("adm_reject")} onAction={(id, a) => act("withdrawals", id, a)} /> : "—"}
              </td>
            </tr>
          ))}
        </Table>
      )}

      {tab === "loans" && (
        <Table testid="admin-loans-table" head={[t("adm_customer"), t("d_amount"), t("adm_emi_tenure"), t("adm_bank_details"), t("d_status"), t("adm_action")]} empty={loans.length === 0 ? t("adm_no_loans") : null}>
          {loans.map((l) => (
            <tr key={l.id} className="border-b border-[#E5E2D9] last:border-0 hover:bg-[#FDFCF8]" data-testid={`admin-loan-row-${l.id}`}>
              <td className="px-6 py-4">
                <div className="font-semibold text-[#1A1A1A]">{l.user_name}</div>
                <div className="text-xs text-[#5C5C5C]">{l.user_email}</div>
              </td>
              <td className="px-6 py-4 tabular font-bold">{inr(l.amount)}</td>
              <td className="px-6 py-4 tabular text-sm text-[#5C5C5C] whitespace-nowrap">
                {inr(l.emi)} × {l.tenure_months} {t("d_months")}
                {l.emis && (
                  <div className="text-xs font-bold text-[#B5952F] mt-0.5" data-testid={`admin-loan-emi-progress-${l.id}`}>
                    {l.emis.filter((e) => e.status === "paid").length}/{l.emis.length} {t("d_emi_progress")}
                  </div>
                )}
              </td>
              <td className="px-6 py-4" data-testid={`admin-loan-bank-${l.id}`}>
                {l.bank_details ? (
                  <div className="text-xs leading-5 text-[#5C5C5C]">
                    <div className="font-semibold text-[#1A1A1A]">{l.bank_details.account_holder}</div>
                    <div>{l.bank_details.bank_name}</div>
                    <div className="tabular">A/C {l.bank_details.account_number}</div>
                    <div className="tabular">IFSC {l.bank_details.ifsc}</div>
                  </div>
                ) : "—"}
              </td>
              <td className="px-6 py-4"><StatusPill status={l.status} /></td>
              <td className="px-6 py-4">
                {l.status === "pending" ? (
                  <ActionBtns id={l.id} onAction={(id, a) => act("loans", id, a, t("msg_loan_updated"))} />
                ) : "—"}
              </td>
            </tr>
          ))}
        </Table>
      )}

      {tab === "loans" && emiVerifications.length > 0 && (
        <div className="mt-6">
          <h3 className="font-display text-xl font-bold text-[#1A1A1A] mb-4">{t("adm_emi_verifications")}</h3>
          <Table testid="admin-emi-table" head={[t("adm_customer"), t("adm_emi_no"), t("d_amount"), t("d_reference_id"), t("adm_view_proof"), t("adm_action")]} empty={null}>
            {emiVerifications.map(({ loan, emi }) => (
              <tr key={`${loan.id}-${emi.n}`} className="border-b border-[#E5E2D9] last:border-0 hover:bg-[#FDFCF8]" data-testid={`admin-emi-row-${loan.id}-${emi.n}`}>
                <td className="px-6 py-4">
                  <div className="font-semibold text-[#1A1A1A]">{loan.user_name}</div>
                  <div className="text-xs text-[#5C5C5C] tabular">{t("d_loan_title")} {inr(loan.amount)}</div>
                </td>
                <td className="px-6 py-4 font-bold text-[#1A1A1A]">#{emi.n}</td>
                <td className="px-6 py-4 tabular font-bold">{inr(emi.amount)}</td>
                <td className="px-6 py-4 tabular text-sm text-[#5C5C5C]">{emi.reference_id || "—"}</td>
                <td className="px-6 py-4">
                  {emi.proof_path ? (
                    <a href={`${API}/files/${emi.proof_path}`} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[#B5952F] hover:underline" data-testid={`emi-proof-link-${loan.id}-${emi.n}`}>
                      {t("adm_view_proof")}
                    </a>
                  ) : "—"}
                </td>
                <td className="px-6 py-4">
                  <ActionBtns id={`${loan.id}-${emi.n}`} onAction={(id, a) => actEmi(loan.id, emi.n, a)} />
                </td>
              </tr>
            ))}
          </Table>
        </div>
      )}

      {tab === "payouts" && (
        <Table testid="admin-payouts-table" head={[t("adm_dsa"), t("dsad_month"), t("d_business"), t("dsad_current_slab"), t("slab_total"), t("d_status"), t("adm_action")]} empty={payouts.length === 0 ? t("adm_no_payouts") : null}>
          {payouts.map((p) => (
            <tr key={p.id} className="border-b border-[#E5E2D9] last:border-0 hover:bg-[#FDFCF8]" data-testid={`admin-payout-row-${p.id}`}>
              <td className="px-6 py-4">
                <div className="font-semibold text-[#1A1A1A]">{p.dsa_name}</div>
                <div className="text-xs text-[#5C5C5C] tabular">{p.dsa_code}</div>
              </td>
              <td className="px-6 py-4 tabular font-semibold">{p.month}</td>
              <td className="px-6 py-4 tabular">{inr(p.business)}</td>
              <td className="px-6 py-4 tabular">{p.slab_pct}%</td>
              <td className="px-6 py-4 tabular font-bold text-[#B5952F]">{inr(p.earnings?.total)}</td>
              <td className="px-6 py-4"><StatusPill status={p.status} /></td>
              <td className="px-6 py-4">
                {p.status === "accrued" ? (
                  <button
                    onClick={() => act("dsa-payouts", p.id, "paid", t("msg_payout_paid"))}
                    className="bg-[#089950] text-white text-xs font-bold px-3 py-1.5 hover:bg-[#067540] transition-colors duration-200"
                    data-testid={`payout-paid-${p.id}`}
                  >
                    {t("adm_mark_paid")}
                  </button>
                ) : (
                  <button
                    onClick={() => act("dsa-payouts", p.id, "accrued")}
                    className="border border-[#1A1A1A] text-[#1A1A1A] text-xs font-bold px-3 py-1.5 hover:bg-[#1A1A1A] hover:text-[#D4AF37] transition-colors duration-200"
                    data-testid={`payout-unpaid-${p.id}`}
                  >
                    {t("adm_mark_unpaid")}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}

      {tab === "offers" && (
        <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-offers">
          <div className="bg-white border border-[#E5E2D9] p-7" data-testid="admin-offer-form">
            <h3 className="font-display text-xl font-bold text-[#1A1A1A] mb-5">{t("adm_offers_title")}</h3>
            <label className={labelCls}>{t("adm_offer_en")}</label>
            <input value={offerForm.text_en} onChange={(e) => setOfferForm({ ...offerForm, text_en: e.target.value })} className={inputCls} placeholder="Festive Bonanza: +1% extra return this month" data-testid="offer-en-input" />
            <div className="mt-4">
              <label className={labelCls}>{t("adm_offer_gu")}</label>
              <input value={offerForm.text_gu} onChange={(e) => setOfferForm({ ...offerForm, text_gu: e.target.value })} className={inputCls} placeholder="તહેવાર ઓફર: આ મહિને +1% વધારાનું વળતર" data-testid="offer-gu-input" />
            </div>
            <button
              onClick={addOffer}
              disabled={!offerForm.text_en.trim()}
              className="mt-5 bg-[#1A1A1A] text-[#D4AF37] px-8 py-3.5 text-sm font-bold tracking-wide hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors duration-300 disabled:opacity-50"
              data-testid="offer-add-button"
            >
              {t("adm_add_offer")}
            </button>
            <p className="text-xs text-[#5C5C5C] mt-3">Shows instantly as a gold running strip at the top of the landing page. Hide or delete anytime.</p>
          </div>
          <div className="bg-white border border-[#E5E2D9]" data-testid="admin-offers-list">
            {offers.length === 0 ? (
              <div className="px-6 py-8 text-center text-[#5C5C5C] text-sm">{t("adm_no_offers")}</div>
            ) : (
              offers.map((o) => (
                <div key={o.id} className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[#E5E2D9] last:border-0" data-testid={`admin-offer-row-${o.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-[#1A1A1A] text-sm truncate">{o.text_en}</div>
                    {o.text_gu && <div className="text-xs text-[#5C5C5C] truncate mt-0.5">{o.text_gu}</div>}
                    <span className={`inline-block mt-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${o.active ? "bg-[#E9F7EF] text-[#089950]" : "bg-[#F3F1EB] text-[#5C5C5C]"}`} data-testid={`admin-offer-status-${o.id}`}>
                      {o.active ? t("adm_show") : t("adm_hide")}
                    </span>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => toggleOffer(o)} className="border border-[#1A1A1A] text-[#1A1A1A] text-xs font-bold px-3 py-1.5 hover:bg-[#1A1A1A] hover:text-[#D4AF37] transition-colors duration-200" data-testid={`offer-toggle-${o.id}`}>
                      {o.active ? t("adm_hide") : t("adm_show")}
                    </button>
                    <button onClick={() => deleteOffer(o)} className="bg-[#E03131] text-white text-xs font-bold px-3 py-1.5 hover:bg-[#b02424] transition-colors duration-200" data-testid={`offer-delete-${o.id}`}>
                      {t("adm_delete")}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {tab === "settings" && (
        <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-settings">
          <div className="bg-white border border-[#E5E2D9] p-7" data-testid="admin-bank-form">
            <h3 className="font-display text-xl font-bold text-[#1A1A1A] mb-5">{t("adm_bank_details")}</h3>
            {[
              { k: "upi_id", l: t("adm_upi_id") },
              { k: "bank_name", l: t("adm_bank_name") },
              { k: "account_name", l: t("adm_account_name") },
              { k: "account_number", l: t("adm_account_number") },
              { k: "ifsc", l: t("adm_ifsc") },
              { k: "admin_alert_email", l: t("adm_alert_email") },
            ].map((f) => (
              <div key={f.k} className="mb-4">
                <label className={labelCls}>{f.l}</label>
                <input
                  value={payForm[f.k]}
                  onChange={(e) => setPayForm({ ...payForm, [f.k]: e.target.value })}
                  className={inputCls}
                  data-testid={`settings-${f.k}-input`}
                />
              </div>
            ))}
            <button
              onClick={saveSettings}
              className="bg-[#1A1A1A] text-[#D4AF37] px-8 py-3.5 text-sm font-bold tracking-wide hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors duration-300"
              data-testid="settings-save-button"
            >
              {t("adm_save")}
            </button>
          </div>
          <div className="bg-white border border-[#E5E2D9] p-7 text-center" data-testid="admin-qr-card">
            <h3 className="font-display text-xl font-bold text-[#1A1A1A] mb-5">{t("adm_current_qr")}</h3>
            <img
              src={payment?.qr_path ? `${API}/settings/qr?v=${encodeURIComponent(payment.qr_path)}` : QR_PAYMENT.url}
              alt="Current payment QR"
              className="h-56 mx-auto object-contain border border-[#E5E2D9]"
              data-testid="settings-qr-preview"
            />
            <input ref={qrRef} type="file" accept="image/*" onChange={uploadQr} className="hidden" data-testid="settings-qr-input" />
            <button
              onClick={() => qrRef.current?.click()}
              disabled={qrBusy}
              className="mt-5 bg-[#D4AF37] text-[#1A1A1A] px-8 py-3.5 text-sm font-bold tracking-wide hover:bg-[#1A1A1A] hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50"
              data-testid="settings-qr-button"
            >
              {t("adm_change_qr")}
            </button>
            <p className="text-xs text-[#5C5C5C] mt-3">JPG / PNG / WEBP, max 5 MB. Customers see the new QR instantly.</p>
          </div>
        </div>
      )}

      {tab === "passbook" && (
        <div className="bg-white border border-[#E5E2D9] p-7 max-w-xl" data-testid="admin-passbook-form">
          <h3 className="font-display text-xl font-bold text-[#1A1A1A] mb-5">{t("adm_manual_title")}</h3>
          <label className={labelCls}>{t("adm_customer")}</label>
          <select value={pbForm.user_id} onChange={(e) => setPbForm({ ...pbForm, user_id: e.target.value })} className={inputCls} data-testid="passbook-user-select">
            <option value="">{t("adm_select_customer")}</option>
            {approvedCustomers.map((u) => (
              <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
            ))}
          </select>
          <div className="mt-4">
            <label className={labelCls}>{t("adm_amount_hint")}</label>
            <input type="number" value={pbForm.amount} onChange={(e) => setPbForm({ ...pbForm, amount: e.target.value })} className={inputCls} placeholder="1000 or -500" data-testid="passbook-amount-input" />
          </div>
          <div className="mt-4">
            <label className={labelCls}>{t("adm_note")}</label>
            <input value={pbForm.note} onChange={(e) => setPbForm({ ...pbForm, note: e.target.value })} className={inputCls} placeholder="Bonus / correction" data-testid="passbook-note-input" />
          </div>
          <button
            onClick={addEntry}
            disabled={!pbForm.user_id || !pbForm.amount}
            className="mt-5 bg-[#1A1A1A] text-[#D4AF37] px-8 py-3.5 text-sm font-bold tracking-wide hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors duration-300 disabled:opacity-50"
            data-testid="passbook-entry-submit"
          >
            {t("adm_add_entry")}
          </button>
        </div>
      )}
      {profile && (
        <div className="fixed inset-0 z-50 bg-[#1A1A1A]/60 backdrop-blur-sm overflow-y-auto p-4 sm:p-8" data-testid="user-profile-modal" onClick={() => setProfile(null)}>
          <div className="bg-[#F3F1EB] w-full max-w-4xl mx-auto border border-[#E5E2D9] my-8" onClick={(e) => e.stopPropagation()}>
            <div className="bg-[#1A1A1A] px-7 py-5 flex items-center justify-between">
              <div>
                <h3 className="font-display text-xl font-bold text-white" data-testid="profile-name">{profile.user.name}</h3>
                <div className="text-xs text-white/60 mt-0.5">{profile.user.email} · {profile.user.phone || "—"}</div>
              </div>
              <button onClick={() => setProfile(null)} className="text-white/70 hover:text-[#D4AF37] text-xl font-bold px-2 transition-colors duration-200" data-testid="profile-close-button">✕</button>
            </div>
            <div className="p-7 space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E2D9] border border-[#E5E2D9]">
                {[
                  { l: t("adm_balance"), v: inr(profile.balance), id: "profile-balance" },
                  { l: t("d_status"), v: profile.user.status, id: "profile-status" },
                  { l: t("adm_joined"), v: (profile.user.created_at || "").slice(0, 10), id: "profile-joined" },
                  { l: t("adm_role"), v: profile.user.role, id: "profile-role" },
                ].map((x) => (
                  <div key={x.l} className="bg-white p-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#B5952F]">{x.l}</div>
                    <div className="font-bold text-[#1A1A1A] mt-1 tabular capitalize" data-testid={x.id}>{x.v}</div>
                  </div>
                ))}
              </div>
              <div className="bg-white border border-[#E5E2D9] p-5" data-testid="profile-bank">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#B5952F] mb-3">{t("adm_bank_details")}</div>
                {profile.user.bank_details ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-[#1A1A1A]">
                    <div><span className="text-[#5C5C5C]">{t("d_ac_holder")}:</span> <b>{profile.user.bank_details.account_holder}</b></div>
                    <div><span className="text-[#5C5C5C]">{t("d_bank_name")}:</span> <b>{profile.user.bank_details.bank_name}</b></div>
                    <div className="tabular"><span className="text-[#5C5C5C]">{t("d_ac_number")}:</span> <b>{profile.user.bank_details.account_number}</b></div>
                    <div className="tabular"><span className="text-[#5C5C5C]">{t("d_ifsc")}:</span> <b>{profile.user.bank_details.ifsc}</b></div>
                  </div>
                ) : (
                  <div className="text-sm text-[#5C5C5C]">—</div>
                )}
              </div>
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white border border-[#E5E2D9] p-5" data-testid="profile-plans">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#B5952F] mb-3">{t("adm_plans")} ({profile.investments.length})</div>
                  {profile.investments.length === 0 ? <div className="text-sm text-[#5C5C5C]">—</div> : (
                    <div className="space-y-2">
                      {profile.investments.map((i) => (
                        <div key={i.id} className="flex items-center justify-between text-sm border-b border-[#F3F1EB] last:border-0 pb-2" data-testid={`profile-plan-${i.id}`}>
                          <div>
                            <span className="font-semibold text-[#1A1A1A] capitalize">{i.plan_type.replace(/_/g, " ")}</span>
                            <span className="text-xs text-[#5C5C5C] ml-2 tabular">{i.rate_label}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="tabular font-bold">{inr(i.principal)}</span>
                            <StatusPill status={i.status} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="bg-white border border-[#E5E2D9] p-5" data-testid="profile-loans">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#B5952F] mb-3">{t("adm_loans")} ({profile.loans.length})</div>
                  {profile.loans.length === 0 ? <div className="text-sm text-[#5C5C5C]">—</div> : (
                    <div className="space-y-2">
                      {profile.loans.map((l) => (
                        <div key={l.id} className="flex items-center justify-between text-sm border-b border-[#F3F1EB] last:border-0 pb-2" data-testid={`profile-loan-${l.id}`}>
                          <div>
                            <span className="tabular font-bold">{inr(l.amount)}</span>
                            <span className="text-xs text-[#5C5C5C] ml-2 tabular">{inr(l.emi)} × {l.tenure_months}</span>
                            {l.emis && <span className="text-xs font-bold text-[#B5952F] ml-2">{l.emis.filter((e) => e.status === "paid").length}/{l.emis.length} {t("d_emi_progress")}</span>}
                          </div>
                          <StatusPill status={l.status} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="bg-white border border-[#E5E2D9] p-5" data-testid="profile-payments">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#B5952F] mb-3">{t("adm_payments")}</div>
                  {profile.deposits.length === 0 && profile.withdrawals.length === 0 ? <div className="text-sm text-[#5C5C5C]">—</div> : (
                    <div className="space-y-2">
                      {[...profile.deposits.map((d) => ({ ...d, _t: "deposit" })), ...profile.withdrawals.map((w) => ({ ...w, _t: "withdrawal" }))]
                        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
                        .slice(0, 10)
                        .map((p) => (
                          <div key={`${p._t}-${p.id}`} className="flex items-center justify-between text-sm border-b border-[#F3F1EB] last:border-0 pb-2" data-testid={`profile-payment-${p._t}-${p.id}`}>
                            <span className="text-xs font-bold uppercase text-[#5C5C5C]">{p._t}</span>
                            <div className="flex items-center gap-3">
                              <span className={`tabular font-bold ${p._t === "deposit" ? "text-[#089950]" : "text-[#E03131]"}`}>
                                {p._t === "deposit" ? "+" : "−"}{inr(p.amount)}
                              </span>
                              <StatusPill status={p.status} />
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
                <div className="bg-white border border-[#E5E2D9] p-5" data-testid="profile-passbook">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#B5952F] mb-3">{t("adm_passbook_recent")}</div>
                  {profile.passbook.length === 0 ? <div className="text-sm text-[#5C5C5C]">—</div> : (
                    <div className="space-y-2">
                      {profile.passbook.slice(0, 10).map((e, i) => (
                        <div key={e.id || i} className="flex items-center justify-between text-sm border-b border-[#F3F1EB] last:border-0 pb-2">
                          <div>
                            <span className="text-xs text-[#5C5C5C] tabular">{e.entry_date}</span>
                            <span className="text-xs text-[#5C5C5C] ml-2">{e.note}</span>
                          </div>
                          <span className={`tabular font-bold ${e.amount >= 0 ? "text-[#089950]" : "text-[#E03131]"}`}>{e.amount >= 0 ? "+" : "−"}{inr(Math.abs(e.amount))}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashShell>
  );
}
